import { Tooltip } from '@patternfly/react-core';
import * as React from 'react';

import {
  SURVEY_SECTIONS,
  Submission,
  SurveyView,
  formatScore,
  sectionAverage,
  summarise
} from '@app/model/Survey';
import { AverageCell, DistributionBar, QuestionLabel, ToneLegend } from '@app/Classes/SurveyParts';

export interface SurveyDistributionProps {
  survey: SurveyView;
  submissions: readonly Submission[];
}

/**
 * Every question as one bar: how the answers were spread, what they average to, and how many gave
 * one.
 *
 * The aggregate reading of the same data the matrix shows per respondent. Two things it does that
 * the matrix cannot: the width of a row is fixed however many students answered, so it stays
 * readable on a class of forty; and a split is visible as a split - two fives and two ones average
 * to the same three as four threes, and only here do those look different.
 *
 * What it gives up is who: nothing on this tab attributes an answer to anybody, which is also why
 * it is the layout that would survive being shown to a reader without `admin` if the service ever
 * served the aggregate. It does not today - the submissions it is computed from are admin only.
 */
export const SurveyDistribution: React.FunctionComponent<SurveyDistributionProps> = ({
  survey,
  submissions
}) => (
  <>
    <ToneLegend showYesNo />
    <div className="cas-survey-scroll">
      <table className="cas-survey-distribution">
        <caption className="pf-v6-screen-reader">
          How each survey question was answered across every respondent
        </caption>
        <thead>
          <tr>
            <th scope="col" className="cas-survey-qcol">Question</th>
            <th scope="col">Answers</th>
            <th scope="col" className="cas-survey-avgcol">
              <Tooltip content="The mean of the answers held here. Not the score above, which is the survey platform's own and is computed differently.">
                <span>Avg</span>
              </Tooltip>
            </th>
          </tr>
        </thead>

        {SURVEY_SECTIONS.map(section => {
          const computed = sectionAverage(section, submissions);
          const reported = section.score ? (survey[section.score] as number | undefined) : undefined;

          return (
            <tbody key={section.key}>
              <tr className="cas-survey-section">
                <th scope="colgroup" colSpan={3}>
                  {section.title}
                  {section.score && (
                    <Tooltip content="The survey platform's own score for this section. It is reported, not recalculated here, so it need not equal the average of the answers beside it.">
                      <span className="cas-survey-section-score">reported {formatScore(reported)}</span>
                    </Tooltip>
                  )}
                  {computed !== undefined && (
                    <Tooltip content="The mean of every rating in this section, over the submissions held here.">
                      <span className="cas-survey-section-score">computed {formatScore(computed)}</span>
                    </Tooltip>
                  )}
                </th>
              </tr>

              {section.questions.map(question => {
                const summary = summarise(question, submissions);
                return (
                  <tr key={String(question.field)}>
                    <th scope="row" className="cas-survey-qcol">
                      <QuestionLabel question={question} />
                    </th>
                    <td className="cas-survey-barcol">
                      <DistributionBar summary={summary} />
                    </td>
                    <td className="cas-survey-avgcol">
                      <AverageCell summary={summary} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          );
        })}
      </table>
    </div>
  </>
);
